import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Badge, Button, Group, Select, Text, Tooltip } from '@mantine/core'
import type { ComboboxData } from '@mantine/core'
import { orderSessions } from '@racedash/shared'
import type { SessionLike } from '@racedash/shared'
import type { AttachedEvent, DashboardSourceState, Selection } from './useDashboardSource'

/**
 * What you are looking at, and how to change it.
 *
 * Deliberately small. The dashboard is the page; this is chrome, and three
 * full-height labelled dropdowns above a race is three dropdowns' worth of
 * screen not showing timing.
 *
 * The sessions offered are only the ones the team's entries claim. Everything
 * else captured at the same meeting is one disclosure away rather than mixed
 * in — a weekend at Spa produces Fun Cup, Belcar and Ligier sessions, and a
 * team that ran one of them should not have to read three. Hiding is safe to do
 * aggressively because it is a relevance filter and not a permission: all
 * derived timing is readable by anyone signed in, so a wrong guess costs a
 * click.
 */
export default function SourceBar({
  state,
  selection,
  onChange,
  cars,
  defaultCar,
  onPinCar,
  onResolveSeries,
  eventsHref,
}: {
  state: DashboardSourceState
  selection: Selection
  onChange: (next: Selection) => void
  /** Flat, or grouped with the team's own cars first. */
  cars: ComboboxData
  defaultCar?: string
  /** Absent for anyone who may not edit this dashboard. */
  onPinCar?: (car: string | null) => void
  onResolveSeries?: () => void
  /** Where the team's entries are edited. */
  eventsHref?: string
}) {
  // The cars entered here without a series, for saying which need one.
  const noSeries = (state.event?.entries ?? []).filter((e) => !e.seriesId).map((e) => e.number)


  const eventOptions = useMemo(
    () =>
      (state.events ?? []).map((e) => ({
        value: e.eventId,
        label: e.startsAt ? `${e.name} — ${e.startsAt.toLocaleDateString()}` : e.name,
      })),
    [state.events],
  )

  /*
   * One dropdown, two groups: the team's own series, then the event's other
   * configured series. Each series is read from the one channel the event's
   * administrator set for it, so a race appears once, under its own name, and
   * nothing here knows or asks about channels.
   */
  const ours = useMemo(() => orderSessions(state.ours), [state.ours])
  const others = useMemo(() => orderSessions(state.others), [state.others])

  const sessionOptions: ComboboxData = [
    // First, and outside the groups: the mode, not a session. What it is
    // showing right now rides in the label, so the list says both that the
    // dashboard is following along and what it has landed on.
    ...(state.auto || ours.length > 0
      ? [{ value: autoValue, label: state.auto ? `Auto: ${nameOf(state.auto)}` : 'Auto' }]
      : []),
    ...(ours.length > 0
      ? [{ group: 'Our sessions', items: ours.map((x) => ({ value: x.id, label: nameOf(x) })) }]
      : []),
    ...(others.length > 0
      ? [
          {
            group: 'Other sessions',
            items: others.map((x) => ({ value: x.id, label: nameOf(x) })),
          },
        ]
      : []),
  ]
  const sessionCount = ours.length + others.length

  return (
    <>
      <Group gap="sm" wrap="wrap" align="center">
        <Select
          aria-label="Event"
          placeholder={eventOptions.length ? 'Event' : 'Nothing entered yet'}
          data={eventOptions}
          value={state.event?.eventId ?? null}
          // Changing event drops the session with it. Carrying a session id
          // across would name a bucket belonging to a different weekend, which
          // resolves to nothing and reads as "no data" rather than as a stale
          // selection.
          onChange={(v) => onChange({ ...selection, eventId: v, sessionId: null })}
          disabled={eventOptions.length === 0}
          searchable
          size="sm"
          w={260}
        />

        <Select
          aria-label="Session"
          placeholder={sessionCount ? 'Auto' : 'Nothing captured yet'}
          data={sessionOptions}
          // The pick where there is one, Auto otherwise — never the session
          // Auto landed on, which looked exactly like having chosen it.
          value={
            selection.sessionId
              ? [...state.ours, ...state.others].some((x) => x.id === selection.sessionId)
                ? selection.sessionId
                : null
              : sessionCount
                ? autoValue
                : null
          }
          onChange={(v) => onChange({ ...selection, sessionId: v === autoValue || !v ? null : v })}
          disabled={sessionCount === 0}
          searchable
          size="sm"
          w={280}
          maxDropdownHeight={420}
        />

        {state.session && (
          <Badge color={state.freshness.live ? 'green' : 'gray'} variant="light">
            {state.freshness.label}
          </Badge>
        )}

        <Select
          aria-label="Car"
          placeholder={cars.length ? 'Car' : 'No cars yet'}
          data={cars}
          value={selection.car ?? null}
          onChange={(v) => onChange({ ...selection, car: v })}
          disabled={cars.length === 0}
          searchable
          clearable
          size="sm"
          w={150}
        />

        {/* Pinning is a statement about the dashboard, not about this viewing:
            it decides which car the widgets mean, and which of two live
            sessions to open on. So it is an explicit act, and only an editor's
            to make. */}
        {onPinCar && selection.car && selection.car !== defaultCar && (
          <Tooltip label="Open this dashboard on this car in future" withArrow>
            <Button
              variant="subtle"
              size="compact-sm"
              color="gray"
              onClick={() => onPinCar(selection.car ?? null)}
            >
              Pin {selection.car}
            </Button>
          </Tooltip>
        )}
        {onPinCar && defaultCar && selection.car === defaultCar && (
          <Tooltip label="Stop opening on this car" withArrow>
            <Button
              variant="subtle"
              size="compact-sm"
              color="gray"
              onClick={() => onPinCar(null)}
            >
              Pinned: {defaultCar}
            </Button>
          </Tooltip>
        )}

      </Group>

      {state.unmatched === 'waiting' && (
        <Alert color="gray" variant="light" mt="sm" title="Nothing from your series yet">
          <Text size="sm">
            Other series have been on track here, but yours has not run yet. Your sessions will
            appear here as soon as they start.
          </Text>
        </Alert>
      )}
      {state.unmatched === 'noSeries' && (
        <Alert color="yellow" variant="light" mt="sm" title="Your entry needs a series">
          <Text size="sm">
            {noSeries.length === 1 ? `Car ${noSeries[0]}` : `Cars ${noSeries.join(', ')}`} at this
            event {noSeries.length === 1 ? 'has' : 'have'} no series set, so the timing here cannot
            be matched to {noSeries.length === 1 ? 'it' : 'them'}. On the Events page, edit your
            entry for this event and choose the series in Series.
          </Text>
          {eventsHref && (
            <Button component={Link} to={eventsHref} size="compact-sm" mt="xs">
              Go to Events
            </Button>
          )}
        </Alert>
      )}
      {state.unmatched === 'unconfigured' && (
        <Alert color="yellow" variant="light" mt="sm" title="Your series' timing feed is not set up here">
          <Text size="sm">
            Timing is being captured at this event, but nobody has said yet which of its feeds
            carries your series, so none of it can be shown as yours. That is set by a racedash
            administrator on the event in the admin site, not by your team.
          </Text>
        </Alert>
      )}
      {state.unmatched === 'unnamed' && (
        <Alert color="yellow" variant="light" mt="sm" title="A session here may be yours under another name">
          <Text size="sm">
            Something is running on your series' channel under a name your series does not list.
            If it is yours, an administrator can add that name on the Series page.
          </Text>
          {onResolveSeries && (
            <Button size="compact-sm" mt="xs" onClick={onResolveSeries}>
              Pick which are ours
            </Button>
          )}
        </Alert>
      )}
    </>
  )
}

/** The session list's entry for following along. Not a bucket id, which never starts with a dot. */
const autoValue = '.auto'

/** How a session reads in a list: the series and the session, as the stream names them. */
function nameOf(s: Pick<SessionLike, 'id' | 'seriesNameRaw' | 'sessionNameRaw'>): string {
  return [s.seriesNameRaw, s.sessionNameRaw].filter(Boolean).join(' — ') || s.id
}

/** Says what an event is doing right now, for an empty dashboard's benefit. */
export function eventStatus(event: AttachedEvent | null, now: Date): string | null {
  if (!event) return null
  const start = event.startsAt?.getTime()
  const end = event.endsAt?.getTime()
  if (start == null || end == null) return null

  const nowMs = now.getTime()
  if (nowMs < start) {
    const days = Math.ceil((start - nowMs) / 86_400_000)
    if (days <= 1) return `${event.name} starts tomorrow.`
    return `${event.name} starts in ${days} days.`
  }
  if (nowMs > end) return `${event.name} has finished.`
  return `${event.name} is running now.`
}
